import T from "@/components/forms/TextField.module.css";

import { ariaTanstackFieldProps } from "@/components/forms/ariaTanstackFieldProps.tsx";
import { WSelect, type ConfiguredWSelectProps } from "@/components/forms/WSelect.tsx";
import { UnconvertedCurrencyBalance } from "@/components/l10n/Currency.tsx";
import { LeftRight } from "@/components/LeftRight.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { ModalResult } from "@/components/modal/ModalResult.ts";
import { WModalContent } from "@/components/modal/WModal.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useConfig } from "@/config.ts";
import { paymentEvent, useAnalytics, type PaymentInfo } from "@/modules/analytics/analytics.ts";
import { AllFees } from "@/modules/billing/AllFees.tsx";
import { baInvoiceQueryAtom } from "@/modules/billing/billingInvoicesQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { isUnverified, useAccountMethods, type BoundMethod } from "@/modules/billing/BoundMethod.ts";
import { ChooseMethod } from "@/modules/billing/choose_method/ChooseMethod.tsx";
import { StatusDisplay } from "@/modules/billing/choose_method/StatusDisplay.tsx";
import {
    t_PopupFailError,
    useAddMethodWithProcessors,
    usePopupProgress,
} from "@/modules/billing/choose_method/useAddMethod.tsx";
import { getPayableInvoiceId, invoiceName } from "@/modules/billing/invoice/invoiceLogic.tsx";
import { lastUsedMethodId } from "@/modules/billing/payment/payment.store";
import {
    PaymentResult,
    type PayableInvoiceId,
    type PaymentActions,
    type PaymentParams,
} from "@/modules/billing/payment/PaymentParams.ts";
import { invalidatePaymentQueries } from "@/modules/billing/payment/paymentUtils.ts";
import { getPaymentReturnUrl } from "@/modules/billing/payment/urls.ts";
import { ErrorTexts, PaymentResponseTakesTimeNotice } from "@/modules/billing/payment_forms/components.tsx";
import { PaymentMethodTitle } from "@/modules/billing/paymentMethodLogic.tsx";
import type { PaymentMethod } from "@/modules/billing/paymentMethods.ts";
import { addableMethodsAtom } from "@/modules/billing/paymentProcessorsLogic.tsx";
import { PaymentPopup } from "@/modules/billing/popup/popup.ts";
import { getShowCardGatewayInfo } from "@/modules/billing/utils.tsx";
import type { DisplayAmount, SiteAmount } from "@/modules/currency/currencyTypes.ts";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { notifyErrorCustom } from "@/modules/error/errorReporting.ts";
import { getEnglishText, SimpleLocalizedError, type Translatable } from "@/modules/l10n/getTransText.ts";
import { errorToReactMessage } from "@/modules/notifications/errorToReactMessage.tsx";
import { raiseBackgroundErrorToast, raiseToast } from "@/modules/notifications/toast.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { UnmountedError } from "@/utils/react/useUnmountSignal.tsx";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import * as tanstackForm from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import type { BillingAccountInvoice, PaymentTarget } from "@warrenio/api-spec/spec.oats.gen";
import { filterNulls } from "@warrenio/utils/collections/filterNulls";
import { notNull } from "@warrenio/utils/notNull";
import { useAtomValue, useStore } from "jotai/react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Input, Label, NumberField, type PressEvent } from "react-aria-components";
import invariant from "tiny-invariant";

//#region Payment method selection

const ADD_METHOD = Symbol.for("MethodSelect/ADD_METHOD");
type ADD_METHOD = typeof ADD_METHOD;

const ADD_METHOD_KEY = "__add_method__";
type ADD_METHOD_KEY = typeof ADD_METHOD_KEY;

type SelectableMethodKey = BoundMethod["id"] | ADD_METHOD_KEY;

function validateSelectedMethodKey(key: SelectableMethodKey | null | undefined, methods: BoundMethod[]) {
    if (key === ADD_METHOD_KEY || key == null || methods.some((m) => m.id === key && !isUnverified(m))) {
        return key;
    } else {
        console.warn(
            "Invalid default value %o for selected method (available %o)",
            key,
            methods.map((m) => m.id),
        );
        return null;
    }
}

function PaymentMethodSelect(props: ConfiguredWSelectProps<BoundMethod | ADD_METHOD, SelectableMethodKey>) {
    const { items } = props;

    const { t } = useLingui();

    const showGateway = getShowCardGatewayInfo(filterNulls(items.map((m) => (m !== ADD_METHOD ? m.method.id : null))));

    return (
        <WSelect
            placeholder={t`Select a payment method`}
            getKey={(item) => (item === ADD_METHOD ? ADD_METHOD_KEY : item.id)}
            getTextValue={(item) => {
                return item === ADD_METHOD ? "Add method" : item.method.name; // no-translate
            }}
            isItemDisabled={(item) => item !== ADD_METHOD && isUnverified(item)}
            {...props}
        >
            {(item) =>
                item === ADD_METHOD ? (
                    <Trans>Add new payment method...</Trans>
                ) : (
                    <LeftRight
                        icon={<span className="min-w-12">{item.method.icon({ isSelected: false })}</span>}
                        title={<PaymentMethodTitle method={item.method} card={item.card} showGateway={showGateway} />}
                        right={
                            isUnverified(item) && (
                                <span className="text-error">
                                    <Trans>Unverified</Trans>
                                </span>
                            )
                        }
                    />
                )
            }
        </WSelect>
    );
}

//#endregion

function usePaymentMethods(account: EBillingAccount): {
    /** Methods that are available to be added to the account */
    newMethods: PaymentMethod[];
    /** Methods that have already been added to the account */
    existingMethods: BoundMethod[];
} {
    const addableMethods = useAtomValue(addableMethodsAtom);
    const { cardMethods, otherMethods } = useAccountMethods(account);
    return useMemo(() => {
        const otherMethodIds = new Set(otherMethods.map((m) => m.id));

        return {
            newMethods: addableMethods
                // Filter out existing methods
                // NB: Adding a new credit card is always OK, even if there are existing cards
                .filter((method) => !otherMethodIds.has(method.id))
                // Only show methods that can be used for making payments (eg. not invoice-by-email)
                .filter((method) => method.PaymentForm),

            existingMethods: [...cardMethods, ...otherMethods].filter((m) => m.method.PaymentForm),
        };
    }, [addableMethods, cardMethods, otherMethods]);
}

interface TopUpInputs {
    amount: DisplayAmount;
}

export interface PaymentModalProps {
    account: EBillingAccount;
    defaultValue?: SelectableMethodKey | null;
    invoice_id?: PayableInvoiceId;
}

const WaitResponseModal = () => (
    <WModalContent title={<Trans>Please be patient</Trans>} modalAction={undefined} className="flex flex-col gap-3">
        <PaymentResponseTakesTimeNotice additionalMessage={<Trans>You may close this dialog window for now.</Trans>} />
    </WModalContent>
);

export const TopUpAmountLabel = ({
    currency,
    minimumAmount,
}: {
    currency: { title: string };
    minimumAmount: ReactNode;
}) => (
    <Trans>
        Enter the top up amount in {currency.title} (minimum {minimumAmount}):
    </Trans>
);

export default function PaymentModal(props: PaymentModalProps) {
    const { invoice_id } = props;

    if (invoice_id != null) {
        return (
            <ContentLoadingSuspense>
                <InvoicePaymentModalLoader {...props} invoice_id={invoice_id} />
            </ContentLoadingSuspense>
        );
    }

    return <PaymentModalContent {...props} />;
}

function InvoicePaymentModalLoader(props: PaymentModalProps & { invoice_id: PayableInvoiceId }) {
    const { invoice_id, ...contentProps } = props;
    const invoices = useSuspenseQueryAtom(baInvoiceQueryAtom(props.account.id));
    const invoice = notNull(invoices.get(props.invoice_id), "invoice");

    return <PaymentModalContent {...contentProps} invoice={invoice} />;
}

interface PaymentModalContentProps extends Omit<PaymentModalProps, "invoice_id"> {
    invoice?: BillingAccountInvoice;
}

function PaymentModalContent({ account, defaultValue, invoice }: PaymentModalContentProps) {
    //#region Hooks
    const { t } = useLingui();

    const { minimumTopupAmount, siteCurrency } = useConfig();
    const queryClient = useQueryClient();
    const store = useStore();

    const analytics = useAnalytics();
    const analyticsTxId = useGenerateUuid();

    const [error, setError] = useState<ReactNode | null>(null);
    const [showWaitMessage, setShowWaitMessage] = useState(false);
    const [hideActionButton, setHideActionButton] = useState(false);

    const currency = useCurrentCurrency();
    // NB: Round to display currency digits. We do not want displayed amounts and internal form amounts to differ (initially).
    const minimumTopupInDisplayCurrency = currency.ceil(currency.fromSite(minimumTopupAmount));

    //#region Form
    const form = useForm({
        defaultValues: {
            amount: minimumTopupInDisplayCurrency,
        } satisfies TopUpInputs,
    });
    const isSubmitting = tanstackForm.useStore(form.store, (s) => s.isSubmitting);
    //#endregion

    const [paymentActionsUnavailable, setPaymentActionsUnavailable] = useState(true);
    const paymentActionsRef = useRef<PaymentActions>(null);

    //#region Method selection
    const { existingMethods, newMethods } = usePaymentMethods(account);
    const adder = useAddMethodWithProcessors(newMethods, { account });

    defaultValue ??= store.get(lastUsedMethodId);
    // Default to "the" method if there is only one available
    if (!defaultValue && existingMethods.length === 1 && !isUnverified(existingMethods[0])) {
        defaultValue = existingMethods[0].id;
    }

    const [methodKeyInternal, setMethod] = useState<SelectableMethodKey | null>(defaultValue ?? null);
    const methodKey = useMemo(
        () => validateSelectedMethodKey(methodKeyInternal, existingMethods),
        [methodKeyInternal, existingMethods],
    );
    const selectedMethod =
        methodKey != null && methodKey !== ADD_METHOD_KEY
            ? notNull(
                  existingMethods.find((item) => item.id === methodKey),
                  "selectedMethod",
              )
            : null;
    //#endregion

    const progress = usePopupProgress(invoice ? "Invoice payment" : "Top-up");

    //#endregion - Hooks

    if (invoice) {
        const invoiceId = getPayableInvoiceId(invoice, account);
        if (invoiceId === null) {
            throw new SimpleLocalizedError(<Trans>Invoice is not payable</Trans>);
        }
    }

    function toastAndSetError(e: unknown, prefix: Translatable) {
        raiseBackgroundErrorToast(e, prefix);

        // TODO: Better way to get/show the text of specific payment Errors
        setError(errorToReactMessage(e));
    }

    function reportAndSetError(message: Translatable) {
        notifyErrorCustom(getEnglishText(message), {
            tags: ["payment"],
            context: { method: selectedMethod?.id },
        });
        setError(message);
    }

    async function makePayment({ amount }: TopUpInputs, ev: PressEvent): Promise<ModalResult> {
        invariant(selectedMethod != null, "Method must be selected");
        const { type, card, id } = selectedMethod;

        store.set(lastUsedMethodId, id);

        let popup: PaymentPopup | undefined;
        // PS! Possible problem: "setupAmount" variable holds different values (total sum with tax when it's payment for invoice,
        // and amount without tax when doing top-up - for Omise to query final amount from backend)
        const setupAmount: SiteAmount = invoice ? (invoice.totals.total as SiteAmount) : currency.toSite(amount);

        let target: PaymentTarget;
        if (invoice) {
            invariant(invoice.id !== "current");
            target = { type: "invoice", invoice_id: invoice.id };
        } else {
            target = { type: "credit", amount: currency.toSite(amount) };
        }

        const paymentParams: PaymentParams = {
            account,
            currency: siteCurrency,
            setupAmount,
            progress: (status) => progress.update(popup, status),
            popup: null,
            returnUrl: getPaymentReturnUrl(),
            getCard() {
                invariant(type === "creditcard", "Method must be a credit card");
                return card;
            },
            target,
        };

        // TODO: Handle errors in-line
        const actions = notNull(paymentActionsRef.current, "paymentActionsRef");

        try {
            // NB: The popup must be opened synchronously (before anything `async`), otherwise the browser will block it
            if (actions.needsPopUp?.(paymentParams)) {
                popup = PaymentPopup.open(ev.pointerType !== "virtual");
                if (!popup) {
                    reportAndSetError(t_PopupFailError);
                    return ModalResult.KEEP_OPEN;
                }

                paymentParams.popup = popup;
            }

            const info: PaymentInfo = {
                transaction_id: analyticsTxId,
                // Always in site currency, not display currency
                currency: paymentParams.currency,
                amount: invoice ? (invoice.totals.total_before_tax as SiteAmount) : currency.toSite(amount),
                invoice: invoice ? { id: invoice.id as number, title: invoiceName(invoice, "long") } : undefined,
                method: selectedMethod.method,
            };

            analytics.event(paymentEvent("begin", info));

            const result = await actions.makePayment(paymentParams);

            if (result === PaymentResult.SUCCESS || result === PaymentResult.WAIT_RESPONSE) {
                // XXX: We consider "waiting" a success, but this might not be technically correct...
                analytics.event(paymentEvent("success", info));
            }

            switch (result) {
                case PaymentResult.SUCCESS:
                    // Automatically close popup on success
                    popup?.close();

                    raiseToast({
                        type: "success",
                        icon: "jp-wallet-icon",
                        message: invoice ? (
                            <Trans>Invoice has been paid</Trans>
                        ) : (
                            <Trans>Your billing account has been topped up</Trans>
                        ),
                    });

                    await invalidatePaymentQueries(queryClient, account.id);

                    return ModalResult.CLOSE;

                case PaymentResult.WAIT_RESPONSE:
                    setShowWaitMessage(true);
                    return ModalResult.KEEP_OPEN;

                case PaymentResult.CLOSE_MANUALLY:
                    setHideActionButton(true);
                    return ModalResult.KEEP_OPEN;

                case PaymentResult.CANCELLED:
                    reportAndSetError(<Trans>Payment was cancelled</Trans>);
                    progress.clear();
                    return ModalResult.KEEP_OPEN;

                case PaymentResult.VALIDATION_FAILED:
                    return ModalResult.KEEP_OPEN;
            }
        } catch (e) {
            console.warn("Caught payment exception", e);
            if (e instanceof UnmountedError) {
                // Dialog was closed while in progress...
            } else {
                toastAndSetError(e, <Trans>Payment error</Trans>);

                // TODO: Force navigate the popup to an error page?
            }
        } finally {
            popup?.dispose();
        }

        // Keep the modal open if there was a failure
        return ModalResult.KEEP_OPEN;
    }

    const PaymentForm = selectedMethod?.method.PaymentForm;
    const isActionHidden =
        hideActionButton || (selectedMethod?.method.type === "link" && !!selectedMethod.method.isInstructionsOnly);

    const isActionDisabled = methodKey === null || (methodKey === ADD_METHOD_KEY && !adder.hasSelectedMethod);
    // NB: If there is no method selected, we are not actually loading anything
    // NB: `isActionLoading` should never be `false`, as that breaks the automatic loading indicator for `modalAction`
    const isActionLoading = !isActionDisabled && paymentActionsUnavailable ? true : undefined;

    const invoiceAmountBeforeTax = invoice ? currency.toSite(invoice.totals.total_before_tax) : currency.toSite(0);

    return showWaitMessage ? (
        <WaitResponseModal />
    ) : (
        <WModalContent
            title={invoice ? t`Pay invoice` : t`Top up your account`}
            label={
                isActionHidden
                    ? undefined
                    : methodKey === ADD_METHOD_KEY
                      ? t`Add payment method`
                      : invoice
                        ? t`Pay invoice`
                        : t`Top up`
            }
            modalAction={
                isActionHidden
                    ? undefined
                    : async (ev) => {
                          // Reset error state on each action
                          setError(null);

                          if (methodKey === ADD_METHOD_KEY) {
                              if (!adder.hasSelectedMethod || !(await adder.validate(ev))) {
                                  return ModalResult.KEEP_OPEN;
                              }

                              try {
                                  const result = await adder.addPaymentMethod({ account });

                                  // Ignore the result since we are keeping the modal open in any case
                                  void result;
                              } catch (e) {
                                  toastAndSetError(e, <Trans>Error adding payment method</Trans>);
                                  return ModalResult.KEEP_OPEN;
                              }

                              setMethod(adder.props.value!);

                              // NB: Keep the modal open in case the method wants to display a custom top-up form
                              return ModalResult.KEEP_OPEN;
                          } else {
                              if (!invoice) {
                                  await form.validateAllFields("submit");
                                  if (!form.state.isFormValid) {
                                      return ModalResult.KEEP_OPEN;
                                  }
                              }

                              return await makePayment(form.state.values, ev);
                          }
                      }
            }
            // Do not allow clicking button before the payment method form has loaded
            isActionLoading={isActionLoading}
            isActionDisabled={isActionDisabled}
            className="flex flex-col gap-3 max-w-30rem"
        >
            {invoice ? (
                <AllFees
                    billingAccount={account}
                    invoice={invoice}
                    amount={invoiceAmountBeforeTax}
                    method={selectedMethod}
                    isInvoicePayment={true}
                />
            ) : (
                <>
                    <form.Field
                        name="amount"
                        validators={{
                            onBlur: ({ value }) =>
                                value < minimumTopupInDisplayCurrency ? t`Amount is too low` : null,
                        }}
                    >
                        {(api) => (
                            <NumberField
                                className={T.NumberField}
                                autoFocus
                                isDisabled={isSubmitting}
                                formatOptions={{
                                    maximumFractionDigits: currency.decimal_digits,
                                    signDisplay: "never",
                                }}
                                {...ariaTanstackFieldProps(api)}
                            >
                                <Label className={T.Label}>
                                    <TopUpAmountLabel
                                        currency={currency}
                                        minimumAmount={
                                            <UnconvertedCurrencyBalance
                                                value={minimumTopupInDisplayCurrency}
                                                currency={currency.code}
                                            />
                                        }
                                    />
                                </Label>
                                <div className="w-50%">
                                    <Input className={T.Input} />
                                </div>
                                <ErrorTexts errors={api.state.meta.errors} />
                            </NumberField>
                        )}
                    </form.Field>

                    <form.Subscribe selector={(s) => s.values.amount}>
                        {(amount) => (
                            <AllFees
                                billingAccount={account}
                                amount={currency.toSite(amount)}
                                method={selectedMethod}
                            />
                        )}
                    </form.Subscribe>
                </>
            )}

            <div>
                <Separator />
            </div>

            <p className="font-size-title">
                <Trans>Payment method</Trans>
            </p>

            <div>
                <PaymentMethodSelect
                    isDisabled={isSubmitting}
                    items={[...existingMethods, ADD_METHOD]}
                    valueKey={methodKey}
                    defaultValueKey={null}
                    onChange={(_, k) => {
                        setMethod(k);
                        setHideActionButton(false);
                    }}
                />
            </div>

            {methodKey === ADD_METHOD_KEY && (
                <ChooseMethod
                    {...adder.props}
                    onChange={(r) => {
                        setPaymentActionsUnavailable(!r);
                        adder.props.onChange(r);
                    }}
                    isDisabled={isSubmitting}
                />
            )}

            {PaymentForm && (
                <ContentLoadingSuspense>
                    <form.Subscribe selector={(s) => s.values.amount}>
                        {(amount) => (
                            <PaymentForm
                                actionsRef={(r) => {
                                    paymentActionsRef.current = r;
                                    setPaymentActionsUnavailable(!r);
                                }}
                                account={account}
                                isSubmitting={isSubmitting}
                                amount={invoice ? invoiceAmountBeforeTax : currency.toSite(amount)}
                            />
                        )}
                    </form.Subscribe>
                </ContentLoadingSuspense>
            )}

            <StatusDisplay statusAtom={progress.statusAtom} />

            {error != null && (
                <div className="text-error max-w-50vw">
                    <Trans>Error</Trans>: {error}
                </div>
            )}
        </WModalContent>
    );
}
